import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Navigation from '@/components/Navigation/Navigation';
import ButtonBackHeader from '@/components/ui/Button/ButtonBackHeader';
import ParentSubpageShell from '@/components/Parent/ParentSubpageShell';
import BaseInputPassword from '@/components/ui/Input/BaseInputPassword';
import CircleLoader from '@/components/ui/Loader/CircleLoader';
import usePatch from '@/hooks/request/usePatch';
import { route } from '@/constants/routeName';

const schema = yup.object().shape({
	old_password: yup.string().required('Current password is a required field').min(8, 'Password min 8 characters'),
	new_password: yup.string().required('New password is a required field').min(8, 'Password min 8 characters'),
	confirm_password: yup.string().required('Confirm password is a required field').min(8, 'Password min 8 characters'),
});

export default function UpdatePassword() {
	const navigate = useNavigate();
	const innerHeight = useSelector((state) => state.content.innerHeight);
	const accountType = useSelector((state) => state.auth.accountType);
	const user = useSelector((state) => state.auth.user);
	const isParent = accountType === 'parent';

	const {
		handleSubmit,
		control,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			email: '',
			password: '',
		},
	});

	const {
		mutate: updatePassword,
		isSuccess,
		isPending,
	} = usePatch({
		url: `users/${user.id}/update_password`,
		method: 'put',
		callback: () => {
			toast.success('Successfully changed password');
			navigate(`/${route.account}`);
		},
		onError: () => {
			toast.error('There is an Error when updating password');
		},
		showError: true,
	});

	const onSubmit = (formData) => {
		if (!user) return;
		updatePassword(formData);
	};

	const form = (
		<form onSubmit={handleSubmit(onSubmit)} className={isParent ? 'subpage-form' : 'flex flex-col gap-4'}>
			<BaseInputPassword name='old_password' label='Current' control={control} errors={errors} />
			<BaseInputPassword name='new_password' label='New Password' control={control} errors={errors} />
			<BaseInputPassword name='confirm_password' label='Confirm Password' control={control} errors={errors} />
			<button
				className={isParent ? 'main full' : 'btn-actions mt-8'}
				type='submit'
				disabled={!user || isPending}
				style={isParent ? { marginTop: 12 } : undefined}
			>
				Confirm {(isSuccess || isPending) && <CircleLoader />}
			</button>
		</form>
	);

	if (isParent) {
		return (
			<ParentSubpageShell
				title='Update password'
				backTo={`/${route.account}`}
				backLabel='← Back to account'
				intro={`This updates the password for your parent login${user?.email ? ` (${user.email})` : ''}, not a student password.`}
			>
				{form}
			</ParentSubpageShell>
		);
	}

	return (
		<div style={{ minHeight: innerHeight }} className='app-container student-page'>
			<div className='flex flex-col w-full h-full gap-12 p-4 pb-24'>
				<ButtonBackHeader />
				<div className='relative flex items-center justify-center'>
					<span className='font-bold'>Update Password</span>
				</div>
				<p className='text-sm text-[var(--weak)]' data-testid='password-account-hint'>
					This updates the password for your account login
					{user?.email ? ` (${user.email})` : ''}.
				</p>
				{form}
			</div>
			<Navigation />
		</div>
	);
}
